import { Anchor, Button, Card, List, Text } from '@mantine/core'
import { useState } from 'react'

import type { Suspension } from '../../api/client'
import { Section } from '../../components/SectionHeader'
import { StatusBanner } from '../../components/StatusBanner'
import { useAuth } from '../../auth/AuthContext'
import { SignInForm } from '../../auth/SignInScreen'
import { useSignOutAndClear } from '../../auth/useSignOutAndClear'
import { DeleteAccountAction, formatDeleteDate } from '../account/DeleteAccountAction'
import { LegalPage } from './LegalPage'

/** The developer name as it shows on the Google Play listing (Store presence › Store settings). One constant so a
 * rename there is one line here too. */
const DEVELOPER_NAME = 'Macrotide by Ikaros Dev'

/** Signed out: the explanation, plus a sign-in form that lets someone delete without ever leaving this page. */
function SignedOutSection() {
  return (
    <Section title="Sign in to delete your account" order={3} card={false}>
      <Text size="sm" mb="sm">
        Sign in below with your account&apos;s email to delete it right here — this works even if you never
        finished setting it up.
      </Text>
      <Card padding="lg" maw={360}>
        <SignInForm />
      </Card>
    </Section>
  )
}

/** Signed in: which account, the same delete action as Settings, and a way out if this isn't their account. */
function SignedInSection() {
  const auth = useAuth()
  const signOutAndClear = useSignOutAndClear()
  const [suspended, setSuspended] = useState<Suspension | null>(null)

  return (
    <Section title="Delete your account" order={3} card={false}>
      <Text size="sm" mb="sm">
        Signed in as <Text span fw={600} size="sm">{auth.email}</Text>. Deleting starts the 30-day grace period
        described below for this account.
      </Text>
      {suspended ? (
        <StatusBanner tone="error">
          Your account is already scheduled for deletion on {formatDeleteDate(suspended.deleteAt)}. To appeal, email{' '}
          {suspended.appealEmail}.
        </StatusBanner>
      ) : (
        <DeleteAccountAction onSuspended={setSuspended} />
      )}
      <Button variant="subtle" color="gray" mt="sm" onClick={() => void signOutAndClear()}>
        Not you? Sign out
      </Button>
    </Section>
  )
}

/**
 * `/delete-account`: works signed out (explains, then signs in) or signed in (deletes right here), so it works for
 * Google Play's required deletion link even if someone never finished setup. Rendered outside the ConsentGate
 * (main.tsx) since deleting doesn't need health-data consent. Structure follows Google Play's data-deletion page
 * requirements: app/developer name, the steps before the explanations, then what's deleted vs kept with retention.
 * The source text is also kept in docs/legal/delete-account.md; keep the two in sync.
 */
export function DeleteAccountPage() {
  const auth = useAuth()
  return (
    <LegalPage title="Delete your Macrotide account">
      <Text size="sm" c="dimmed">
        Developer: {DEVELOPER_NAME}
      </Text>

      <Text size="sm">
        You can delete your Macrotide account and everything it holds on our servers at any time. Deleting starts a{' '}
        <b>30-day grace period</b> rather than deleting straight away — see &quot;What happens to your data&quot;
        below.
      </Text>

      <Text size="sm">
        Want a copy first? Download it from{' '}
        <Anchor href="/export/download">macrotide.ikarosdev.com/export/download</Anchor> (a ZIP with your data as
        CSV files and JSON, plus your photos) before you delete.
      </Text>

      <Section title="How to delete" card={false}>
        <List type="ordered" size="sm">
          <List.Item>
            <b>In the app:</b> More › Account and sync › Delete account, and confirm twice.
          </List.Item>
          <List.Item>
            <b>On the web:</b> sign in on this page, then press Delete account and confirm.
          </List.Item>
          <List.Item>
            <b>No access to the email on the account:</b> email macrotide@ikarosdev.com from the account&apos;s own
            address and we&apos;ll delete it for you.
          </List.Item>
        </List>
      </Section>

      {auth.status === 'signedIn' ? <SignedInSection /> : <SignedOutSection />}

      <Section title="What happens to your data" card={false}>
        <Text size="sm" fw={600}>
          Deleted
        </Text>
        <Text size="sm" mb="xs">
          Everything synced to your account: food log, recipes and custom foods, weight and body measurements,
          profile, goals, workouts, programs, equipment, progress photos, AI result reports, any files you
          imported, and your notification push tokens. This happens after the 30-day grace period (signing in again before then cancels it), and is
          gone from our backups within a further 30 days — so a trace can remain for up to 60 days in total.
        </Text>
        <Text size="sm" fw={600}>
          Kept
        </Text>
        <List size="sm">
          <List.Item>
            Products or photos you&apos;ve already sent to Open Food Facts: it publishes those openly under its own
            licence, so we can&apos;t remove them.
          </List.Item>
          <List.Item>
            Whatever&apos;s already on your phone, unless you also turn on &quot;Also delete the data on this
            phone&quot; when you delete there.
          </List.Item>
          <List.Item>Anything Macrotide has written to Health Connect — remove those in Health Connect itself.</List.Item>
          <List.Item>
            If your account was suspended or banned, a one-way hash of your email (plus a masked form, for example
            jo•••@gmail.com, for staff) is kept for 12 months to stop re-registration.
          </List.Item>
          <List.Item>
            If a request of yours was flagged as prohibited content and its review, or a report to the authorities,
            is still open: the evidence copied for it (see the privacy policy, &quot;Content checks and holds&quot;),
            until that&apos;s closed.
          </List.Item>
          <List.Item>
            Emails you sent to macrotide@ikarosdev.com (2 years), counts of how you used features (13 months) and the
            staff audit log (2 years), each for its own period.
          </List.Item>
        </List>
      </Section>

      <Section title="Immediate erasure" card={false}>
        <Text size="sm">
          If you&apos;d rather not wait for the grace period, email macrotide@ikarosdev.com and we&apos;ll erase
          your account straight away.
        </Text>
      </Section>
    </LegalPage>
  )
}
